import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { invoicesAPI } from '../utils/api';
import { useConfirm } from '../hooks/useConfirm';
import RowActionsMenu from '../components/RowActionsMenu';
import { format } from 'date-fns';
import {
  FiPlus, FiSearch, FiFileText, FiSend, FiCopy,
  FiTrash2, FiDownload, FiEye, FiFilter, FiEdit2
} from 'react-icons/fi';
import './Invoices.css';

const Invoices = () => {
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchInvoices();
  }, [pagination.page, status]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoicesAPI.getAll({
        search,
        status,
        page: pagination.page,
        limit: pagination.limit,
      });
      setInvoices(response.data.invoices);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      }));
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchInvoices();
  };

  const handleSend = async (invoice) => {
    const confirmed = await confirm({
      title: 'Mark as Sent',
      message: `Mark invoice ${invoice.invoice_number} as sent?`,
      type: 'info',
      confirmText: 'Mark as Sent',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await invoicesAPI.send(invoice.id);
      toast.success('Invoice marked as sent');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send invoice');
    }
  };

  const handleDuplicate = async (invoice) => {
    try {
      const response = await invoicesAPI.duplicate(invoice.id);
      toast.success('Invoice duplicated successfully');
      // Navigate to the new invoice
      window.location.href = `/invoices/${response.data.invoice.id}`;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to duplicate invoice');
    }
  };

  const handleDelete = async (invoice) => {
    const confirmed = await confirm({
      title: 'Delete Invoice',
      message: `Are you sure you want to delete invoice ${invoice.invoice_number}? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await invoicesAPI.delete(invoice.id);
      toast.success('Invoice deleted successfully');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete invoice');
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await invoicesAPI.getPDF(invoice.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const formatCurrency = (amount, currency = 'AED') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  // Red = overdue on a billable invoice; amber = due within 3 days of an
  // already-sent invoice. Drafts never color the date — they cannot chase.
  const dueDateClass = (invoice) => {
    if (['draft', 'paid', 'cancelled'].includes(invoice.status)) return '';
    if (invoice.days_overdue > 0) return 'text-danger';
    const daysToDue = Math.ceil((new Date(invoice.due_date) - new Date().setHours(0, 0, 0, 0)) / 86400000);
    return daysToDue <= 3 ? 'text-warning' : '';
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      viewed: 'badge-viewed',
      paid: 'badge-paid',
      partial: 'badge-partial',
      overdue: 'badge-overdue',
      cancelled: 'badge-cancelled',
    };
    return statusClasses[status] || 'badge-draft';
  };

  const copyPublicLink = (invoice) => {
    const url = `${window.location.origin}/invoice/${invoice.public_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied to clipboard');
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Invoices</h1>
          <p>Manage your invoices</p>
        </div>
        <Link to="/invoices/create" className="btn btn-primary">
          <FiPlus /> Create Invoice
        </Link>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSearch} className="search-bar">
            <div className="search-input">
              <FiSearch className="search-input-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Search invoices..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="form-control"
              style={{ width: 'auto' }}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setSearchParams(e.target.value ? { status: e.target.value } : {});
              }}
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="viewed">Viewed</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button type="submit" className="btn btn-secondary">
              Search
            </button>
          </form>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="empty-state">
              <FiFileText className="empty-state-icon" />
              <h3 className="empty-state-title">No invoices found</h3>
              <p className="empty-state-text">
                {search || status ? 'Try adjusting your search criteria' : 'Get started by creating your first invoice'}
              </p>
              {!search && !status && (
                <Link to="/invoices/create" className="btn btn-primary">
                  <FiPlus /> Create Invoice
                </Link>
              )}
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Client</th>
                      <th>Issue Date</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          <Link to={`/invoices/${invoice.id}`} className="text-primary font-medium">
                            {invoice.invoice_number}
                          </Link>
                        </td>
                        <td>
                          <div>
                            <div className="font-medium">{invoice.company_name}</div>
                            <div className="text-muted text-sm">{invoice.client_email}</div>
                          </div>
                        </td>
                        <td>{formatDate(invoice.issue_date)}</td>
                        <td>
                          <span className={dueDateClass(invoice)}>
                            {formatDate(invoice.due_date)}
                          </span>
                        </td>
                        <td>
                          <div>
                            <div className="font-medium">{formatCurrency(invoice.total, invoice.currency)}</div>
                            {invoice.amount_due < invoice.total && (
                              <div className="text-muted text-sm">Due: {formatCurrency(invoice.amount_due, invoice.currency)}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(invoice.status)}`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td>
                          <RowActionsMenu
                            actions={[
                              { label: 'View', icon: FiEye, className: 'btn-secondary', primary: true, onClick: () => navigate(`/invoices/${invoice.id}`) },
                              { label: 'Edit', icon: FiEdit2, className: 'btn-secondary', primary: invoice.status === 'draft', show: invoice.status === 'draft', onClick: () => navigate(`/invoices/${invoice.id}/edit`) },
                              { label: 'Send', icon: FiSend, className: 'btn-success', show: invoice.status === 'draft', onClick: () => handleSend(invoice) },
                              { label: 'Download PDF', icon: FiDownload, className: 'btn-secondary', onClick: () => handleDownloadPDF(invoice) },
                              { label: 'Copy Public Link', icon: FiCopy, className: 'btn-secondary', onClick: () => copyPublicLink(invoice) },
                              { label: 'Duplicate', icon: FiCopy, className: 'btn-secondary', onClick: () => handleDuplicate(invoice) },
                              { label: 'Delete', icon: FiTrash2, className: 'btn-danger', danger: true, primary: invoice.status !== 'paid', show: invoice.status !== 'paid', onClick: () => handleDelete(invoice) },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Invoices;